import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import {
  ChakraFlex,
  ChakraImage,
  ChakraSpacer,
  ChakraText,
  ChatUser,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Reply,
  SkeletonLoader,
  TopicToArrow,
  UpDownArrow,
} from '@equip.health/ui';
import { User } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { FileMessage, ThreadInfo, UserMessage } from '@sendbird/chat/message';
import { isEmpty } from 'lodash';

import OverviewLoading from '~/components/common/OverviewLoading';
import RawHTML from '~/components/common/RawHTML';
import { MEMBERS_TO_DISPLAY_IN_REPLY } from '~/lib/constants/chat.constants';
import { S3FileStatus } from '~/lib/constants/s3filestatus.contants';
import { useNoteAttachmentModalContext } from '~/lib/contexts/NoteAttachmentModalContext';
import useChatAttachment from '~/lib/hooks/chat/useChatAttachment';
import { ChatMessage, FileAttachment, MemberMetaData } from '~/lib/types/chat';
import ChatUtil, {
  isImage,
  removeUUIDFromFilename,
} from '~/lib/util/chat/chat.utils';

import AttachmentViewer, { PreviewDocument } from './AttachmentViewer';
import MentionsBox from './MentionsBox';
import MessageKebabMenu from './MessageKebabMenu';
import MessageDotIndicator from './NewMessageIndicator';
import ChannelMembers from '../../common/ChannelMembers';

import './sendbird-reactions.css';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

const idPrefix = 'message';
const idPostfix = 'tile';

const { h6, label, labelBold, paragraph } = EquipUITheme.typography;
const { primary } = EquipUIFireflyV2Theme.colors;

interface MessageReplyRowProps {
  id: string;
  isShowReply: boolean;
  members: ChatUser[];
  onClick: () => void;
}

const MessageReplyRow = ({
  id,
  isShowReply,
  members,
  onClick,
}: MessageReplyRowProps) => {
  const hasMembers = (members ?? []).length > 0;
  return (
    isShowReply && (
      <ChakraFlex
        alignItems="center"
        gridGap="4px"
        id={`${idPrefix}-reply-container__${id}__${idPostfix}`}
        marginTop="12px"
        width="100%"
      >
        <>
          {hasMembers && (
            <ChakraFlex
              alignItems="center"
              gridGap="4px"
              id={`${idPrefix}-reply-wrapper__${id}__${idPostfix}`}
              paddingRight="14px"
            >
              <TopicToArrow
                id={`${idPrefix}-reply-topic-to-arrow__${id}__${idPostfix}`}
              />
              <ChakraText
                color="neutral.secondary"
                {...label}
                id={`${idPrefix}-message-to-label__${id}__${idPostfix}`}
              >
                Message to
              </ChakraText>
              {(members ?? [])
                .slice(0, MEMBERS_TO_DISPLAY_IN_REPLY)
                .map((member) => (
                  <>
                    <ChannelMembers
                      id={id}
                      members={[member]}
                      paddedTop={false}
                    />
                    <ChakraText
                      color="neutral.secondary"
                      {...label}
                      id={`${idPrefix}-member-nickname__${member?.userId}__${idPostfix}`}
                    >
                      {member?.nickname}
                    </ChakraText>
                  </>
                ))}
              {(members ?? []).length > MEMBERS_TO_DISPLAY_IN_REPLY && (
                <ChannelMembers
                  id={id}
                  members={members.slice(
                    MEMBERS_TO_DISPLAY_IN_REPLY,
                    members.length,
                  )}
                  paddedTop={false}
                />
              )}
            </ChakraFlex>
          )}
          <ChakraFlex
            cursor="pointer"
            gridGap="4px"
            id={`${idPrefix}-reply-label-wrapper__${id}__${idPostfix}`}
            onClick={onClick}
          >
            <ChakraText
              color="primary.100"
              {...labelBold}
              id={`${idPrefix}-reply-label__${id}__${idPostfix}`}
            >
              Reply
            </ChakraText>
            <Reply
              fill="teal"
              height="16px"
              id={`${idPrefix}-reply-icon__${id}__${idPostfix}`}
              width="16px"
            />
          </ChakraFlex>
        </>
      </ChakraFlex>
    )
  );
};

interface DocumentMessageProps {
  content: FileMessage;
  id: string;
  isShowKebabMenu: boolean;
  isThreadedMessage: boolean;
  onMessageRemoved: (isRemoved: boolean) => void;
}

const isDownloadableFileValidationStatus = (fileValidationStatus: string) => {
  return [
    S3FileStatus.NEW.toString(),
    S3FileStatus.UNKNOWN.toString(),
    S3FileStatus.CLEAN.toString(),
  ].includes(fileValidationStatus);
};

const DocumentMessage = ({
  content,
  id,
  isShowKebabMenu,
  isThreadedMessage,
  onMessageRemoved,
}: DocumentMessageProps) => {
  const {
    fileValidationStatus,
    getDocumentDownloadURL,
    removeAttachment,
    s3Url,
  } = useChatAttachment();

  const { currentPatientId } = useGlobalContext();

  const [isImageDocumentClicked, setIsImageDocumentClicked] =
    useState<boolean>(false);

  const [isStatusFetched, setIsStatusFetched] = useState<boolean>(false);
  const { setShowNoteAttachmentModal } = useNoteAttachmentModalContext();

  const isImageAttachment = isImage(content?.name);

  const isValidFile = useMemo(() => {
    return (
      isDownloadableFileValidationStatus(fileValidationStatus) &&
      !isEmpty(s3Url)
    );
  }, [s3Url, fileValidationStatus]);

  const isInvalidFile = useMemo(() => {
    return (
      isEmpty(fileValidationStatus) ||
      [S3FileStatus.INVALID.toString()].includes(fileValidationStatus)
    );
  }, [fileValidationStatus]);

  useEffect(() => {
    if (fileValidationStatus) setIsStatusFetched(true);
  }, [fileValidationStatus]);

  useEffect(() => {
    if (isValidFile && isImageDocumentClicked && isStatusFetched) {
      if (s3Url) {
        setShowNoteAttachmentModal({
          name: content?.name,
          url: s3Url,
        });
      }
      setIsImageDocumentClicked(false);
    } else if (
      (isImageDocumentClicked || isImageAttachment) &&
      !isStatusFetched
    ) {
      getDocumentDownloadURL({
        fileName: content?.name,
        patientExternalId: currentPatientId,
        onSuccess: () => {
          onMessageRemoved(false);
        },
        onFailure: () => {
          onMessageRemoved(true);
        },
      });
    }
  }, [isImageDocumentClicked, isStatusFetched]);

  const handleRemoveMessage = () => {
    removeAttachment(
      content?.name,
      () => {
        onMessageRemoved(true);
      },
      () => {
        onMessageRemoved(false);
      },
    );
  };

  if (!s3Url && isImageAttachment && !fileValidationStatus) {
    return <SkeletonLoader boxSize="180px" id={idPrefix} />;
  }

  return (
    <ChakraFlex
      id={`${idPrefix}-message-content-container__${id}__${idPostfix}`}
      overflow="hidden"
    >
      {isImageAttachment && !isInvalidFile ? (
        <ChakraFlex alignItems="baseline">
          <ChakraImage
            alt={content?.name}
            borderRadius="16px"
            boxSize="150px"
            cursor="pointer"
            fit="cover"
            id={`${idPrefix}-message-content__${id}__${idPostfix}`}
            loading="lazy"
            onClick={() => {
              if (isDownloadableFileValidationStatus(fileValidationStatus)) {
                setIsImageDocumentClicked(true);
              }
            }}
            src={s3Url}
            title={removeUUIDFromFilename(content?.name)}
          />
          {!isThreadedMessage && isShowKebabMenu && (
            <MessageKebabMenu
              messageId={content.messageId}
              onRemoveClicked={handleRemoveMessage}
            />
          )}
        </ChakraFlex>
      ) : (
        <ChakraFlex
          alignItems="center"
          backgroundColor="#F2F2F2"
          border="1px solid #ccc"
          borderRadius="10px"
          width="360px"
        >
          <PreviewDocument
            file={content?.name}
            fileValidationStatus={fileValidationStatus}
            isLoading={false}
            isShowClose={false}
            isValidFile={isValidFile}
            onClick={() => {
              if (S3FileStatus.INVALID !== fileValidationStatus) {
                setIsImageDocumentClicked(true);
              }
            }}
          />
          {!isThreadedMessage && isShowKebabMenu && (
            <MessageKebabMenu
              messageId={content.messageId}
              onRemoveClicked={handleRemoveMessage}
            />
          )}
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

interface MessageContentProps {
  content: string;
  id: string;
}
const MessageContent = ({ content, id }: MessageContentProps) => (
  <ChakraText
    {...paragraph}
    id={`${idPrefix}-message-content-container__${id}__${idPostfix}`}
  >
    <RawHTML
      content={content}
      id={`${idPrefix}-message-content__${id}__${idPostfix}`}
    />
  </ChakraText>
);

interface MessageHeaderRowProps {
  createdDate: string;
  id: string;
  isMentioned: boolean;
  isRead: boolean;
  isReverse?: boolean;
  senderName: string;
  timestamp: string;
}

const MessageHeaderRow = React.forwardRef<
  HTMLDivElement,
  MessageHeaderRowProps
>(
  (
    {
      createdDate,
      id,
      isMentioned,
      isRead,
      isReverse = false,
      senderName,
      timestamp,
    }: MessageHeaderRowProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => (
    <ChakraFlex
      alignItems="start"
      direction={isReverse ? 'row-reverse' : 'row'}
      gridGap="8px"
      id={`${idPrefix}-header-container__${id}__${idPostfix}`}
      ref={ref}
      width="100%"
    >
      <ChakraText
        {...h6}
        id={`${idPrefix}-header-sender-name__${id}__${idPostfix}`}
      >
        {senderName}
      </ChakraText>
      {createdDate && (
        <ChakraText
          color="neutral.secondary"
          textTransform="capitalize"
          {...label}
          id={`${idPrefix}-header-created-date__${id}__${idPostfix}`}
        >
          {createdDate}
        </ChakraText>
      )}
      <ChakraText
        color="neutral.secondary"
        {...label}
        id={`${idPrefix}-header-timestamp__${id}__${idPostfix}`}
      >
        {timestamp}
      </ChakraText>
      {!isRead && (
        <MessageDotIndicator
          id={`${idPrefix}-header-new-message-indicator__${id}__${idPostfix}`}
        />
      )}
      <ChakraSpacer />
      {!isRead && isMentioned && (
        <MessageDotIndicator
          id={`${idPrefix}-header-new-mentions-indicator__${id}__${idPostfix}`}
          isMentioned={isMentioned}
        />
      )}
    </ChakraFlex>
  ),
);

MessageHeaderRow.displayName = 'MessageHeaderRow';

interface AdminMessageProps {
  avatarSize: AvatarSize;
  id: string;
  isRead: boolean;
  message: string;
  sender: User;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const AdminMessage = React.forwardRef<HTMLDivElement, AdminMessageProps>(
  (
    { avatarSize, id, isRead, message, sender, unreadRef }: AdminMessageProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const messageContainerProps = {
      alignItems: 'center',
      borderRadius: '16px',
      borderWidth: '1px',
      gridGap: '12px',
      marginBottom: '24px',
      padding: '12px',
      width: '100%',
    };

    const { nickname, metaData } = sender;
    const senderRole = ((metaData ?? {}) as MemberMetaData)?.providerType;

    return (
      <ChakraFlex
        id={`${idPrefix}-admin-message-container__${id}__${idPostfix}`}
        ref={ref}
        {...messageContainerProps}
      >
        {nickname && (
          <ChannelMembers
            id={id}
            members={[sender] as ChatUser[]}
            paddedTop={false}
            showPopoverOnHover
            size={avatarSize}
          />
        )}
        <ChakraFlex
          alignItems="center"
          id={`${idPrefix}-sender-role-container__${id}__${idPostfix}`}
          ref={unreadRef}
          width="100%"
        >
          {nickname && (
            <ChakraFlex
              direction="column"
              id={`${idPrefix}-sender-role-wrapper__${id}__${idPostfix}`}
            >
              <ChakraText
                {...h6}
                data-testid={`${idPrefix}-sender-nickname__${id}__${idPostfix}`}
                id={`${idPrefix}-sender-nickname__${id}__${idPostfix}`}
              >
                {nickname}
              </ChakraText>
              {senderRole && (
                <ChakraText
                  color="neutral.secondary"
                  {...paragraph}
                  data-testid={`${idPrefix}-sender-role__${id}__${idPostfix}`}
                  id={`${idPrefix}-sender-role__${id}__${idPostfix}`}
                >
                  {senderRole}
                </ChakraText>
              )}
            </ChakraFlex>
          )}
          {!isRead && (
            <MessageDotIndicator
              id={`${idPrefix}-new-message__${id}__${idPostfix}`}
            />
          )}
          <ChakraSpacer />
          <ChakraText
            color="neutral.secondary"
            {...label}
            data-testid={`${idPrefix}-admin__${id}__${idPostfix}`}
            id={`${idPrefix}-admin__${id}__${idPostfix}`}
          >
            {message}
          </ChakraText>
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);
AdminMessage.displayName = 'AdminMessage';

interface SingleMessageProps {
  avatarSize: AvatarSize;
  chatUserId: string;
  channel: GroupChannel;
  children?: ReactNode;
  createdAt: number;
  fileMessage?: FileMessage;
  chatMessage?: UserMessage | FileMessage;
  id: string;
  isChannelReadOnly: boolean;
  isMentioned: boolean;
  isMessageSent: boolean;
  isRead: boolean;
  isShowKebabMenu: boolean;
  isShowReply: boolean;
  isThreadedMessage: boolean;
  mentionedUsers: User[];
  message: string;
  noMargin?: boolean;
  onMessageRemoved: (isRemoved: boolean) => void;
  onSendMessage?: (value: ChatMessage) => void;
  patientId: string;
  referenceDate?: number;
  sender: User;
  timestamp: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const SingleMessage = React.forwardRef<HTMLDivElement, SingleMessageProps>(
  (
    {
      avatarSize,
      channel,
      children,
      createdAt,
      fileMessage,
      id,
      isMentioned,
      isMessageSent,
      isRead,
      isShowKebabMenu,
      isShowReply,
      isThreadedMessage,
      mentionedUsers,
      message,
      noMargin = false,
      onMessageRemoved,
      onSendMessage,
      referenceDate,
      sender,
      timestamp,
      unreadRef,
    }: SingleMessageProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const singleMessageContainerProps = {
      alignItems: 'start',
      gridGap: '16px',
      marginBottom: noMargin ? '' : '24px',
      width: '100%',
    };
    const { url, members } = channel;
    const [isShowReplyInput, setIsShowReplyInput] = useState<boolean>(false);
    const [fileToSend, setFileToSend] = useState<FileAttachment>(null);

    const calculatedDate =
      ChatUtil.calculateRelativeDate(referenceDate, true) !==
      ChatUtil.calculateRelativeDate(createdAt, true)
        ? ChatUtil.calculateRelativeDate(createdAt, true)
        : null;

    return (
      <ChakraFlex
        id={`${idPrefix}-single-message-container__${id}__${idPostfix}`}
        ref={ref}
        {...singleMessageContainerProps}
      >
        <ChannelMembers
          id={`${idPrefix}-single-message-members__${id}__${idPostfix}`}
          members={[sender] as ChatUser[]}
          paddedTop={false}
          showPopoverOnHover
          size={avatarSize}
        />
        <ChakraFlex
          direction="column"
          id={`${idPrefix}-single-message-wrapper__${id}__${idPostfix}`}
          width="100%"
        >
          <MessageHeaderRow
            createdDate={calculatedDate}
            id={`${idPrefix}-single-message-header-row__${id}__${idPostfix}`}
            isMentioned={isMentioned}
            isRead={isRead}
            ref={unreadRef}
            senderName={sender?.nickname}
            timestamp={timestamp}
          />
          <ChakraFlex
            _hover={{ background: '#ffffff !important' }}
            className="sendbird-parent-message-info"
            direction="column"
            id={`${idPrefix}-single-message-column-wrapper__${id}__${idPostfix}`}
            padding="5px 10px 5px 10px"
            width="100%"
          >
            <ChakraFlex
              direction="row"
              id={`${idPrefix}-single-message-row-wrapper__${id}__${idPostfix}`}
            >
              {fileMessage ? (
                <DocumentMessage
                  content={fileMessage}
                  id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                  isShowKebabMenu={isShowKebabMenu}
                  isThreadedMessage={isThreadedMessage}
                  onMessageRemoved={onMessageRemoved}
                />
              ) : (
                <MessageContent
                  content={message}
                  id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                />
              )}
            </ChakraFlex>
          </ChakraFlex>
          <MessageReplyRow
            id={`${idPrefix}-single-message-reply__${id}__${idPostfix}`}
            isShowReply={isShowReply && !children && !fileMessage}
            members={mentionedUsers as ChatUser[]}
            onClick={() => setIsShowReplyInput(!isShowReplyInput)}
          />
          {isShowReplyInput && (
            <ChakraFlex
              id={`${idPrefix}-single-message-mentions-box-wrapper__${id}__${idPostfix}`}
              marginTop="10px"
              width="100%"
            >
              <MentionsBox
                autofocus
                channelUrl={url}
                id={`${idPrefix}-single-message-mentions-box__${id}__${idPostfix}`}
                isMessageSent={isMessageSent}
                key={id}
                members={members as ChatUser[]}
                onEnterPressed={(newMessage) => {
                  onSendMessage(newMessage);
                  setFileToSend(null);
                }}
                onFileSelected={(selectedFile) => {
                  setFileToSend(selectedFile);
                }}
                updatedAttachment={fileToSend}
              />
            </ChakraFlex>
          )}
          {fileToSend && (
            <AttachmentViewer
              attachments={[fileToSend.file]}
              id={id}
              isLoading={isMessageSent}
              onRemove={() => {
                setFileToSend(null);
              }}
            />
          )}
          {children}
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);
SingleMessage.displayName = 'SingleMessage';

interface ThreadedMessageProps {
  avatarSize: AvatarSize;
  chatUserId: string;
  channel: GroupChannel;
  chatMessage: FileMessage | UserMessage;
  createdAt: number;
  fileMessage?: FileMessage;
  id: string;
  isChannelReadOnly: boolean;
  isMentioned: boolean;
  isMessageSent: boolean;
  isNewReply: boolean;
  isRead: boolean;
  isShowKebabMenuForReplies: boolean;
  isShowReply: boolean;
  mentionedUsers: User[];
  message: string;
  messageId: number;
  onInputFocus: (inputRef: React.MutableRefObject<HTMLInputElement>) => void;
  onMessageRemoved: (messageId: number, replyId?: number) => void;
  onRepliesExpanded?: () => void;
  onSendMessage?: (value: ChatMessage) => void;
  patientId: string;
  replies: ChatMessage[];
  sender: User;
  threadInfo: ThreadInfo;
  timestamp: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const ThreadedMessage = React.forwardRef<HTMLDivElement, ThreadedMessageProps>(
  (
    {
      avatarSize,
      chatUserId,
      channel,
      chatMessage,
      createdAt,
      fileMessage,
      id,
      isChannelReadOnly,
      isMentioned,
      isMessageSent,
      isNewReply,
      isRead,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isShowKebabMenuForReplies,
      isShowReply,
      mentionedUsers,
      message,
      messageId,
      onInputFocus,
      onMessageRemoved,
      onRepliesExpanded,
      onSendMessage,
      patientId,
      replies,
      sender,
      threadInfo,
      timestamp,
      unreadRef,
    }: ThreadedMessageProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { url, members } = channel;
    const [fileToSend, setFileToSend] = useState<FileAttachment>(null);

    return (
      <SingleMessage
        avatarSize={avatarSize}
        channel={channel}
        chatMessage={chatMessage}
        chatUserId={chatUserId}
        createdAt={createdAt}
        fileMessage={fileMessage}
        id={`${idPrefix}-threaded-message-main__${id}__${idPostfix}`}
        isChannelReadOnly={isChannelReadOnly}
        isMentioned={isMentioned}
        isMessageSent={isMessageSent}
        isRead={isRead}
        isShowKebabMenu={isShowKebabMenuForReplies}
        isShowReply={false}
        isThreadedMessage
        mentionedUsers={mentionedUsers}
        message={message}
        onMessageRemoved={() => onMessageRemoved(messageId)}
        onSendMessage={onSendMessage}
        patientId={patientId}
        ref={ref}
        referenceDate={createdAt}
        sender={sender}
        timestamp={timestamp}
        unreadRef={unreadRef}
      >
        <ChakraFlex
          alignItems="start"
          borderColor="neutral.line"
          borderRadius="16px"
          borderWidth="1px"
          direction="column"
          id={`${idPrefix}-threaded-message-container__${id}__${idPostfix}`}
          padding="13px"
        >
          <ChakraFlex
            alignItems="center"
            cursor="pointer"
            gridGap="8px"
            id={`${idPrefix}-threaded-message-wrapper__${id}__${idPostfix}`}
            onClick={() => {
              setIsExpanded(!isExpanded);
              if (!isExpanded) onRepliesExpanded?.();
            }}
            padding="6px"
            width="100%"
          >
            <Reply
              height="16px"
              id={`${idPrefix}-threaded-message-reply__${id}__${idPostfix}`}
              width="16px"
            />
            {threadInfo?.replyCount > 0 && (
              <ChakraText
                color="neutral.secondary"
                {...labelBold}
                id={`${idPrefix}-threaded-message-thread-info__${id}__${idPostfix}`}
              >
                Replies
              </ChakraText>
            )}
            {isNewReply && (
              <MessageDotIndicator
                id={`${idPrefix}-threaded-message-new-message-indicator__${id}__${idPostfix}`}
              />
            )}
            <ChakraSpacer />
            <ChakraFlex
              gridGap="8px"
              id={`${idPrefix}-threaded-message-expand-collapse-wrapper__${id}__${idPostfix}`}
            >
              <ChakraText
                color="primary.100"
                {...labelBold}
                id={`${idPrefix}-threaded-message-expand-collapse-label__${id}__${idPostfix}`}
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </ChakraText>
              <UpDownArrow
                fill={primary[100]}
                height="16px"
                id={`${idPrefix}-threaded-message-expand-collapse-arrow__${id}__${idPostfix}`}
                width="16px"
              />
            </ChakraFlex>
          </ChakraFlex>
          {isExpanded && (
            <ChakraFlex
              alignItems={(replies ?? []).length > 0 ? 'start' : 'center'}
              direction="column"
              gridGap="16px"
              id={`${idPrefix}-threaded-message-expand-collapse-container__${id}__${idPostfix}`}
              padding="6px"
              width="100%"
            >
              {(replies ?? []).length > 0 ? (
                <>
                  {replies
                    .filter((reply) => !reply.isRemoved)
                    .map((reply) => (
                      <SingleMessage
                        avatarSize="sm"
                        channel={channel}
                        chatMessage={
                          reply.isFileMessage
                            ? (reply.sbMessage as FileMessage)
                            : (reply.sbMessage as UserMessage)
                        }
                        chatUserId={chatUserId}
                        fileMessage={
                          reply.isFileMessage
                            ? (reply.sbMessage as FileMessage)
                            : null
                        }
                        id={`${idPrefix}-threaded-message-expand-collapse-reply__${reply?.messageId?.toFixed()}__${idPostfix}`}
                        isChannelReadOnly={isChannelReadOnly}
                        isMessageSent={isMessageSent}
                        isShowKebabMenu={isShowKebabMenuForReplies}
                        isShowReply={false}
                        isThreadedMessage={false}
                        key={`message-tile-reply-${reply?.messageId}`}
                        noMargin
                        onMessageRemoved={(isRemoved) => {
                          if (isRemoved)
                            onMessageRemoved(messageId, reply?.messageId);
                        }}
                        patientId={patientId}
                        referenceDate={createdAt}
                        {...reply}
                      />
                    ))}
                  {isShowReply && (
                    <MentionsBox
                      channelUrl={url}
                      id={`${idPrefix}-threaded-message-mentions-box__${id}__${idPostfix}`}
                      isMessageSent={isMessageSent}
                      key={id}
                      members={members as ChatUser[]}
                      onEnterPressed={(newMessage) => {
                        onSendMessage(newMessage);
                        setFileToSend(null);
                      }}
                      onFileSelected={(selectedFile) => {
                        setFileToSend(selectedFile);
                      }}
                      onFocus={onInputFocus}
                      updatedAttachment={fileToSend}
                    />
                  )}
                  {fileToSend && (
                    <AttachmentViewer
                      attachments={[fileToSend.file]}
                      id={id}
                      isLoading={isMessageSent}
                      onRemove={() => {
                        setFileToSend(null);
                      }}
                    />
                  )}
                </>
              ) : (
                <OverviewLoading />
              )}
            </ChakraFlex>
          )}
        </ChakraFlex>
      </SingleMessage>
    );
  },
);
ThreadedMessage.displayName = 'ThreadedMessage';

interface IndividualChannelMessageProps {
  avatarSize: AvatarSize;
  chatUserId: string;
  channel: GroupChannel;
  createdAt: number;
  chatMessage: UserMessage | FileMessage;
  fileMessage?: FileMessage;
  id: string;
  isChannelReadOnly: boolean;
  isMentioned: boolean;
  isMyMessage: boolean;
  isRead: boolean;
  message: string;
  messageId: number;
  noMargin?: boolean;
  onMessageRemoved: (messageId: number) => void;
  patientId: string;
  referenceDate?: number;
  sender: User;
  timestamp: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const IndividualChannelMessage = React.forwardRef<
  HTMLDivElement,
  IndividualChannelMessageProps
>(
  (
    {
      avatarSize,
      createdAt,
      fileMessage,
      id,
      isMentioned,
      isMyMessage,
      isRead,
      message,
      messageId,
      noMargin = false,
      onMessageRemoved,
      referenceDate,
      sender,
      timestamp,
      unreadRef,
    }: IndividualChannelMessageProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const singleMessageContainerProps = {
      alignItems: 'start',
      gridGap: '16px',
      marginBottom: noMargin ? '' : '24px',
      width: '100%',
    };
    const calculatedDate =
      ChatUtil.calculateRelativeDate(referenceDate, true) !==
      ChatUtil.calculateRelativeDate(createdAt, true)
        ? ChatUtil.calculateRelativeDate(createdAt, true)
        : null;

    return (
      <ChakraFlex
        direction="row-reverse"
        id={`${idPrefix}-single-message-container__${id}__${idPostfix}`}
        ref={ref}
        {...singleMessageContainerProps}
      >
        <ChannelMembers
          id={`${idPrefix}-single-message-members__${id}__${idPostfix}`}
          members={[sender] as ChatUser[]}
          paddedTop={false}
          showPopoverOnHover
          size={avatarSize}
        />
        <ChakraFlex
          alignItems="end"
          direction="column"
          id={`${idPrefix}-single-message-wrapper__${id}__${idPostfix}`}
          width="100%"
        >
          <MessageHeaderRow
            createdDate={calculatedDate}
            id={`${idPrefix}-single-message-header-row__${id}__${idPostfix}`}
            isMentioned={isMentioned}
            isRead={isRead}
            isReverse
            ref={unreadRef}
            senderName={sender?.nickname}
            timestamp={timestamp}
          />
          <ChakraFlex
            _hover={{ background: '#ffffff !important' }}
            className="sendbird-parent-message-info"
            direction="column"
            id={`${idPrefix}-single-message-column-wrapper__${id}__${idPostfix}`}
            width="100%"
          >
            <ChakraFlex
              direction="row-reverse"
              id={`${idPrefix}-single-message-row-wrapper__${id}__${idPostfix}`}
              width="100%"
            >
              {fileMessage ? (
                <DocumentMessage
                  content={fileMessage}
                  id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                  isShowKebabMenu={isMyMessage}
                  isThreadedMessage={false}
                  onMessageRemoved={(isRemoved) => {
                    if (isRemoved) onMessageRemoved(messageId);
                  }}
                />
              ) : (
                <ChakraFlex
                  backgroundColor="primary.10"
                  borderRadius="14px 14px 0px 14px"
                  gridGap="24px"
                  maxWidth="80%"
                  padding="8px"
                >
                  <MessageContent
                    content={message}
                    id={`${idPrefix}-single-message-content__${id}__${idPostfix}`}
                  />
                </ChakraFlex>
              )}
            </ChakraFlex>
          </ChakraFlex>
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);
IndividualChannelMessage.displayName = 'IndividualChannelMessage';

interface PatientOtherMessageTileProps {
  chatUserId: string;
  channel: GroupChannel;
  id: string;
  isChannelReadOnly: boolean;
  isGroupChannel: boolean;
  isMessageSent: boolean;
  message: ChatMessage;
  onInputFocus: (inputRef: React.MutableRefObject<HTMLInputElement>) => void;
  onMessageRemoved: (messageId: number, replyId?: number) => void;
  onRepliesExpanded: (message: ChatMessage, onSuccess?: () => void) => void;
  onSendMessage: (value: ChatMessage) => void;
  patientId: string;
  unreadRef?: React.MutableRefObject<HTMLDivElement>;
}

const PatientOtherMessageTile = React.forwardRef<
  HTMLDivElement,
  PatientOtherMessageTileProps
>(
  (
    {
      chatUserId,
      channel,
      id,
      isChannelReadOnly,
      isGroupChannel,
      isMessageSent,
      message,
      onInputFocus,
      onMessageRemoved,
      onRepliesExpanded,
      onSendMessage,
      patientId,
      unreadRef,
    }: PatientOtherMessageTileProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const fileMessage = message?.isFileMessage
      ? (message?.sbMessage as FileMessage)
      : null;

    if (message?.isAdminMessage)
      return (
        <AdminMessage
          avatarSize="md"
          ref={ref}
          {...message}
          unreadRef={unreadRef}
        />
      );

    if (!isGroupChannel && message?.isMyMessage)
      return (
        <IndividualChannelMessage
          avatarSize="md"
          channel={channel}
          chatMessage={
            fileMessage != null
              ? fileMessage
              : (message.sbMessage as UserMessage)
          }
          chatUserId={chatUserId}
          fileMessage={fileMessage}
          id={id}
          isChannelReadOnly={isChannelReadOnly}
          isRead={message?.isRead && !isChannelReadOnly}
          onMessageRemoved={onMessageRemoved}
          patientId={patientId}
          ref={ref}
          referenceDate={message?.createdAt}
          unreadRef={unreadRef}
          {...message}
        />
      );

    const hasNewReply =
      !!message?.hasNewReply ||
      channel?.myLastRead < message?.threadInfo?.lastRepliedAt;

    if (message?.hasThread)
      return (
        <ThreadedMessage
          avatarSize="md"
          channel={channel}
          chatMessage={message.sbMessage as UserMessage}
          chatUserId={chatUserId}
          fileMessage={fileMessage}
          isChannelReadOnly={isChannelReadOnly}
          isMessageSent={isMessageSent}
          isNewReply={hasNewReply}
          isRead={!hasNewReply && !isChannelReadOnly}
          isShowKebabMenuForReplies={message?.isMyMessage}
          isShowReply={!isChannelReadOnly}
          messageId={message?.messageId}
          onInputFocus={onInputFocus}
          onMessageRemoved={onMessageRemoved}
          onRepliesExpanded={() => onRepliesExpanded(message)}
          onSendMessage={(newMessage) =>
            onSendMessage({
              ...newMessage,
              parentMessageId: message?.messageId,
              thread: message?.thread,
            })
          }
          patientId={patientId}
          ref={ref}
          replies={message?.thread}
          unreadRef={unreadRef}
          {...message}
        />
      );

    return (
      <SingleMessage
        avatarSize="md"
        channel={channel}
        chatMessage={message.sbMessage as UserMessage}
        chatUserId={chatUserId}
        fileMessage={fileMessage}
        id={id}
        isChannelReadOnly={isChannelReadOnly}
        isMessageSent={isMessageSent}
        isRead={message?.isRead && !isChannelReadOnly}
        isShowKebabMenu={message?.isMyMessage}
        isShowReply={isGroupChannel && !isChannelReadOnly}
        isThreadedMessage={false}
        onMessageRemoved={(isRemoved) =>
          isRemoved && onMessageRemoved(message?.messageId)
        }
        onSendMessage={(newMessage) =>
          onSendMessage({ ...newMessage, parentMessageId: message?.messageId })
        }
        patientId={patientId}
        ref={ref}
        referenceDate={message?.createdAt}
        unreadRef={unreadRef}
        {...message}
      />
    );
  },
);

PatientOtherMessageTile.displayName = 'PatientOtherMessageTile';

export default PatientOtherMessageTile;
